import { CalendarToday, CreditCard } from "@mui/icons-material";
import CheckIcon from '@mui/icons-material/Check';
import CrossIcon from '@mui/icons-material/Close';
import { FormControlLabel, Grid, IconButton, TextField as Input, InputAdornment, InputLabel, Radio, RadioGroup } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import { useFormik } from "formik";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'src/services/ApiService';
import AuthorizeService from '../../src/components/api-authorization/AuthorizeService';
import PlanCard from "../components/PlanCard";
import { paySchema } from "../schema";
import { formatCardNumber, formatExpiryDate } from "../utilities/formatting";

const Payment = () => {

    const [userId, setUserId] = useState<any>();
    const [subscriptionId, setSubscriptionId] = useState<any>();
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
    const [formNotFilled, setFormNotFilled] = useState<boolean>(true);
    const [isFailedResponse, setIsFailedResponse] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [planName, setPlanName] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [planPrice, setPlanPrice] = useState<string | null>('');

    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            name: "",
            cardNumber: "",
            expiryDate: "",
            cvv: "",
        },
        validationSchema: paySchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            // Implement payment logic here
            try {
                const response = await axios.post(`UserSubscriptions`, {
                    userId,
                    subscriptionId
                });
                if (response.status >= 200 && response.status < 300) {
                    setIsSuccessful(true);
                    setIsFailedResponse(false);
                    formik.resetForm();
                }

            } catch (error) {
                const err = error as any;
                if (err.response && typeof err.response.data === 'string') {
                    if (err.response?.data?.includes('valid subscription already exists for this user')) {
                        setErrorMessage('Payment failed! You are already subscribed to a plan.');
                    }
                }
                setIsFailedResponse(true);
                console.log('Error while creating new record: ', error);
            }
            setIsSubmitting(false);
        },

    });


    const redirectToHomePage = () => {
        navigate(`/`);
    }

    useEffect(() => {
        if (formik.isValid && formik.dirty) {
            setFormNotFilled(false);
        } else {
            setFormNotFilled(true);
        }
    }, [formik.isValid, formik.dirty]);

    useEffect(() => {
        const getUserDetails = async () => {

            setSubscriptionId(sessionStorage.getItem('subscriptionId'));
            setPlanName(sessionStorage.getItem('planName'));
            setPlanPrice(sessionStorage.getItem('planPrice'));
            sessionStorage.removeItem('subscriptionId');
            sessionStorage.removeItem('planName');
            sessionStorage.removeItem('planPrice');


            const response = await AuthorizeService.getUser();
            if (response) {
                setUserId(response.sub);
            } else {
                setUserId(null);
            }
        };

        getUserDetails();
    }, []);

    return (
        <Box>
            <Grid container spacing={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', m: 2 }}>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            background: "var(--light-grey)",
                            py: 5,
                            px: { xs: 2, md: 7 },
                            maxWidth: "32rem",
                            margin: "0 auto",
                            my: 5,
                            borderRadius: "20px",
                        }}
                    >
                        {/* <SharedLayout> */}
                        <Box>
                            <Typography
                                variant="h5"
                                className="fw-500 text-center"
                                sx={{ pb: 3 }}
                            >
                                Pay with card
                            </Typography>
                            <Box sx={{ pb: 3 }} className="flex justify-content-between">
                                <FormControl>
                                    <RadioGroup
                                        aria-labelledby="demo-radio-buttons-group-label"
                                        defaultValue="credit"
                                        name="radio-buttons-group"
                                        sx={{
                                            ".Mui-checked": {
                                                color: "var(--main-color) !important",
                                            },
                                            span: {
                                                fontWeight: "500",
                                            },
                                        }}
                                    >
                                        <FormControlLabel
                                            value="credit"
                                            control={<Radio />}
                                            label="Credit or Debit Card"
                                        />
                                    </RadioGroup>
                                </FormControl>
                                <Box>
                                    <img
                                        src="/assets/svg/visa.svg"
                                        alt="visa"
                                        width={40}
                                        height={40}
                                    />
                                    <img
                                        src="/assets/svg/master_card.svg"
                                        alt="visa"
                                        width={40}
                                        height={40}
                                    />
                                </Box>
                            </Box>
                            <Box>
                                <form onSubmit={formik.handleSubmit}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12}>
                                            <Box sx={{ pb: 2 }}>
                                                <InputLabel sx={{ py: 0.5, color: "#49454F" }}>
                                                    Cardholder Name
                                                </InputLabel>
                                                <Input
                                                    fullWidth
                                                    id="name"
                                                    name="name"
                                                    value={formik.values.name}
                                                    onChange={(e) => {
                                                        formik.handleChange(e);
                                                    }}
                                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                                    helperText={formik.touched.name && formik.errors.name}
                                                    placeholder="e.g John Doe"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={12}>
                                            <Box sx={{ pb: 2 }}>
                                                <InputLabel sx={{ py: 0.5, color: "#49454F" }}>
                                                    Card Number
                                                </InputLabel>
                                                <Input
                                                    fullWidth
                                                    id="cardNumber"
                                                    name="cardNumber"
                                                    value={formatCardNumber(formik.values.cardNumber)}
                                                    onChange={(e) => {
                                                        e.target.value = formatCardNumber(e.target.value);
                                                        formik.handleChange(e);
                                                    }}
                                                    error={
                                                        formik.touched.cardNumber &&
                                                        Boolean(formik.errors.cardNumber)
                                                    }
                                                    helperText={
                                                        formik.touched.cardNumber && formik.errors.cardNumber
                                                    }
                                                    sx={{
                                                        "& .MuiInputBase-root": {
                                                            paddingLeft: "8px",
                                                        },
                                                    }}
                                                    inputProps={{ maxLength: 19 }}
                                                    placeholder="1234 1234 1234 1234"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton edge="end" sx={{ p: 0, marginRight: "5px" }}>
                                                                    <CreditCard className="main-color" />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <InputLabel sx={{ py: 0.5, color: "#49454F" }}>
                                                Expiry Date
                                            </InputLabel>
                                            <Input
                                                variant="outlined"
                                                fullWidth
                                                id="expiryDate"
                                                name="expiryDate"
                                                value={formik.values.expiryDate}
                                                onChange={(e) => {
                                                    e.target.value = formatExpiryDate(e.target.value);
                                                    formik.handleChange(e);
                                                }}
                                                error={
                                                    formik.touched.expiryDate &&
                                                    Boolean(formik.errors.expiryDate)
                                                }
                                                helperText={
                                                    formik.touched.expiryDate && formik.errors.expiryDate
                                                }
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <CalendarToday
                                                                sx={{ color: "#ABABAB", width: "1rem" }}
                                                            />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                placeholder="MM/YY"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <InputLabel sx={{ py: 0.5, color: "#49454F" }}>
                                                CVV
                                            </InputLabel>
                                            <Input
                                                variant="outlined"
                                                fullWidth
                                                id="cvv"
                                                name="cvv"
                                                value={formik.values.cvv}
                                                onChange={formik.handleChange}
                                                error={formik.touched.cvv && Boolean(formik.errors.cvv)}
                                                helperText={formik.touched.cvv && formik.errors.cvv}
                                                inputProps={{ maxLength: 3 }}
                                                placeholder="e.g 123"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        sx={{
                                            width: "100%",
                                            mt: 4,
                                            py: 1.3,
                                            fontWeight: "500",

                                        }}
                                        className="normal-text"
                                        disabled={formNotFilled}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : `Pay Now $${planPrice}`}
                                    </Button>
                                    {isSuccessful &&
                                        <>
                                            <Alert sx={{ marginTop: '15px' }} icon={<CheckIcon fontSize="inherit" />} severity="success">
                                                Your payment has been successful
                                            </Alert>
                                            <Button variant="contained" onClick={redirectToHomePage} sx={{
                                                width: "100%",
                                                mt: 4,
                                                py: 1.3,
                                                fontWeight: "500",
                                            }}>Continue to the app</Button>
                                        </>
                                    }
                                    {isFailedResponse &&
                                        <Alert sx={{ marginTop: '15px' }} icon={<CrossIcon fontSize="inherit" />} severity="error">
                                            {errorMessage ? errorMessage : "Payment has been failed"}
                                        </Alert>
                                    }
                                </form>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <PlanCard planName={planName} planPrice={planPrice} />
                </Grid>
            </Grid>
        </Box>
    )
}
export default Payment;